// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The artefacts.
 *
 * A report is produced for EVERY run, including one that found nothing — an
 * empty result is a real answer to a real question and it deserves a file, not
 * a blank screen. Every artefact here is text, which is deliberate: text can be
 * rendered inline with a copy button, and that is the only thing that works in
 * an installed iOS home-screen app, where a download from a blob: URL silently
 * does nothing (WebKit 167341).
 */

import { afterIdatNote, describeChunkType } from './graft.ts';
import { COMFY_READER_DATE, COMFY_READER_SOURCE } from './verify.ts';
import { flagSensitiveTerms } from '../recipe/summarise.ts';
import type { Artefact, InspectResult, Recipe } from '../types.ts';

export const TOOL_VERSION = '1.0.0';

function pretty(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

const CARRIER_WORDS: Record<string, string> = {
  tEXt: 'a tEXt chunk',
  zTXt: 'a compressed zTXt chunk',
  iTXt: 'an iTXt chunk',
  comf: 'a private comf chunk',
  'pixel-lsb': 'the low bit of the pixels',
  sidecar: 'a file you supplied',
};

export function carrierWords(carrier: string): string {
  return CARRIER_WORDS[carrier] ?? carrier;
}

/** One sidecar per recovered recipe. */
export function recipeArtefact(recipe: Recipe): Artefact {
  const json = recipe.kind === 'json';
  return {
    name: `${recipe.fileStem}.${json ? 'json' : 'txt'}`,
    mime: json ? 'application/json' : 'text/plain',
    text: json ? pretty(recipe.text) : recipe.text,
  };
}

/**
 * regraft's own portable bundle: everything recovered, with where it came from
 * attached, in the shape a later graft consumes.
 */
export function bundleArtefact(result: InspectResult, sourceName: string): Artefact {
  const payload = {
    tool: 'regraft',
    version: TOOL_VERSION,
    note: 'These entries were RECOVERED from an image, not authored. Each records the carrier it was found in.',
    source: sourceName,
    image: result.imageInfo,
    recipes: result.recipes.map((r) => ({
      keyword: r.keyword,
      generator: r.generator,
      carrier: r.carrier,
      afterIdat: r.afterIdat,
      bytes: r.byteLength,
      certain: r.certain,
      text: r.text,
    })),
  };
  return {
    name: 'recipe.json',
    mime: 'application/json',
    text: JSON.stringify(payload, null, 2),
  };
}

/** Recipes parsed back out of a bundle the tool wrote earlier. */
export function readBundle(text: string): Array<{ keyword: string; text: string }> {
  const parsed: unknown = JSON.parse(text);
  if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { recipes?: unknown }).recipes)) {
    const out: Array<{ keyword: string; text: string }> = [];
    for (const entry of (parsed as { recipes: Array<Record<string, unknown>> }).recipes) {
      if (typeof entry.keyword === 'string' && typeof entry.text === 'string') {
        out.push({ keyword: entry.keyword, text: entry.text });
      }
    }
    if (out.length > 0) return out;
  }
  // Not one of ours. A bare ComfyUI graph is the other thing people have on
  // disk, so recognise it by shape rather than refusing.
  if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { nodes?: unknown }).nodes)) {
    return [{ keyword: 'workflow', text }];
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const values = Object.values(parsed as Record<string, unknown>);
    const isApiGraph =
      values.length > 0 &&
      values.every((v) => !!v && typeof v === 'object' && typeof (v as { class_type?: unknown }).class_type === 'string');
    if (isApiGraph) return [{ keyword: 'prompt', text }];
  }
  throw new Error('That JSON file is not a regraft bundle, a ComfyUI workflow or a ComfyUI prompt graph.');
}

export function reportMarkdown(
  result: InspectResult,
  sourceName: string,
  bytes: number,
  stamp: string,
): Artefact {
  const lines: string[] = [];
  const push = (s = '') => lines.push(s);

  push(`# regraft report`);
  push();
  push(`| | |`);
  push(`|---|---|`);
  push(`| File | \`${sourceName}\` |`);
  push(`| Size | ${bytes.toLocaleString()} bytes |`);
  push(`| Image | ${result.imageInfo} |`);
  push(`| Read at | ${stamp} |`);
  push(`| Tool | regraft ${TOOL_VERSION} |`);
  push();

  push(`## What was found`);
  push();
  if (result.recipes.length === 0) {
    push(
      'No generation recipe. That proves nothing about where this image came from: Discord, Reddit, most chat apps and every re-encode strip these on upload, and a re-encode destroys the hidden pixel plane too.',
    );
  } else {
    for (const recipe of result.recipes) {
      push(
        `### \`${recipe.keyword}\` — ${recipe.generator}${recipe.certain ? '' : ' (best guess)'}`,
      );
      push();
      push(`${recipe.label}. Carried in ${carrierWords(recipe.carrier)}${recipe.afterIdat ? ', after the image data' : ''}. ${recipe.byteLength.toLocaleString()} bytes.`);
      push();
      const sensitive = flagSensitiveTerms(recipe.text);
      if (sensitive.length > 0) {
        push(`This one names: ${sensitive.map((s) => `${s.kind} (\`${s.sample}\`)`).join('; ')}.`);
        push();
      }
      push('```');
      push(recipe.kind === 'json' ? pretty(recipe.text) : recipe.text);
      push('```');
      push();
    }
  }

  push(`## Notes`);
  push();
  for (const finding of result.findings) {
    push(`- **${finding.title}.** ${finding.detail}`);
  }
  push();

  push(`## What ComfyUI will see`);
  push();
  push(
    `Predicted from ${COMFY_READER_SOURCE}, as that reader stood on ${COMFY_READER_DATE}. This is a prediction, not a test against a running copy of ComfyUI.`,
  );
  push();
  if (!result.comfy.completed) {
    push(`- It will load **nothing** from this file.`);
  } else if (result.comfy.sawKeys.length === 0) {
    push(`- It will find no keys in this file.`);
  } else {
    push(`- It will find: ${result.comfy.sawKeys.map((k) => `\`${k}\``).join(', ')}.`);
  }
  for (const note of result.comfy.notes) push(`- ${note}`);
  push();

  push(`## Chunk table`);
  push();
  push(`| # | Type | What it is | Offset | Bytes | Checksum |`);
  push(`|---|---|---|---|---|---|`);
  for (const chunk of result.parse.chunks) {
    push(
      `| ${chunk.index} | \`${chunk.type}\` | ${describeChunkType(chunk.type)}${afterIdatNote(chunk.type, chunk.afterIdat)} | ${chunk.offset.toLocaleString()} | ${chunk.dataLen.toLocaleString()} | ${chunk.crcOk ? 'ok' : 'MISMATCH'} |`,
    );
  }
  if (result.parse.trailerLength > 0) {
    push(`| — | (after IEND) | stray bytes | ${result.parse.trailerStart.toLocaleString()} | ${result.parse.trailerLength.toLocaleString()} | — |`);
  }
  push();

  push(`## What this report does not say`);
  push();
  push(
    '- regraft reports what a file declares about its own origin, and nothing more. It cannot tell you whether an image was made by a model. Anyone can write any chunk into any PNG, so the presence of a ComfyUI graph is not proof; and platforms strip these on upload, so the absence of one is not proof either.',
  );
  push(
    '- regraft does not read, verify, alter or remove camera EXIF, GPS coordinates, timestamps, colour profiles or C2PA content credentials. A file it has touched may still be carrying all of those.',
  );
  push();

  return { name: 'regraft-report.md', mime: 'text/markdown', text: lines.join('\n') };
}

export function reportJson(result: InspectResult, sourceName: string, bytes: number, stamp: string): Artefact {
  const payload = {
    tool: 'regraft',
    version: TOOL_VERSION,
    file: sourceName,
    bytes,
    image: result.imageInfo,
    readAt: stamp,
    recipes: result.recipes.map((r) => ({
      keyword: r.keyword,
      generator: r.generator,
      label: r.label,
      carrier: r.carrier,
      afterIdat: r.afterIdat,
      checksumOk: r.crcOk,
      bytes: r.byteLength,
      certain: r.certain,
      namesThings: flagSensitiveTerms(r.text).map((s) => s.kind),
    })),
    findings: result.findings,
    comfyUiPrediction: {
      source: COMFY_READER_SOURCE,
      asOf: COMFY_READER_DATE,
      completes: result.comfy.completed,
      keys: result.comfy.sawKeys,
      notes: result.comfy.notes,
    },
    chunks: result.parse.chunks.map((c) => ({
      index: c.index,
      type: c.type,
      offset: c.offset,
      bytes: c.dataLen,
      checksumOk: c.crcOk,
      afterImageData: c.afterIdat,
    })),
    trailerBytes: result.parse.trailerLength,
    caveat:
      'regraft reports what a file declares about its own origin. Presence is not proof an image was generated; absence is not proof it was not.',
  };
  return { name: 'regraft-report.json', mime: 'application/json', text: JSON.stringify(payload, null, 2) };
}
